export type PersistentStorageContext = {
  supported: boolean;
  persisted: boolean;
  isSecureContext: boolean;
  isStandalone: boolean;
  /** คำแนะนำตามสภาพแวดล้อม — แสดงใน UI ได้ */
  guidanceKey:
    | "already_persisted"
    | "install_pwa"
    | "use_installed_app"
    | "secure_context_required"
    | "webview_or_browser_denied"
    | "unsupported";
};

function detectPersistentStorageContext(
  persisted: boolean,
): PersistentStorageContext {
  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.storage &&
    typeof navigator.storage.persist === "function";
  const isSecureContext =
    typeof window !== "undefined" ? window.isSecureContext : false;
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      // iOS Safari PWA
      ("standalone" in navigator &&
        (navigator as Navigator & { standalone?: boolean }).standalone ===
          true));

  if (!supported) {
    return {
      supported: false,
      persisted: false,
      isSecureContext,
      isStandalone,
      guidanceKey: "unsupported",
    };
  }

  if (persisted) {
    return {
      supported: true,
      persisted: true,
      isSecureContext,
      isStandalone,
      guidanceKey: "already_persisted",
    };
  }

  if (!isSecureContext) {
    return {
      supported: true,
      persisted: false,
      isSecureContext,
      isStandalone,
      guidanceKey: "secure_context_required",
    };
  }

  if (!isStandalone) {
    return {
      supported: true,
      persisted: false,
      isSecureContext,
      isStandalone,
      guidanceKey: "install_pwa",
    };
  }

  return {
    supported: true,
    persisted: false,
    isSecureContext,
    isStandalone,
    guidanceKey: "webview_or_browser_denied",
  };
}

export function getPersistentStorageGuidance(
  key: PersistentStorageContext["guidanceKey"],
): string {
  switch (key) {
    case "already_persisted":
      return "ອຸປະກອນນີ້ໄດ້ຮັບສິດເກັບຂໍ້ມູນຖາວອນແລ້ວ";
    case "install_pwa":
      return (
        "ເບຣາວເຊີປະຕິເສດ persist() ເພາະເປີດຜ່ານແທັບປົກກະติ — " +
        "ຕິດຕັ້ງແອັບເປັນ PWA (Install app / Add to Home Screen) ແລ້ວເປີດຈາກໄອຄອນແອັບ " +
        "ຈຶ່ງມີໂອກາດໄດ້ສິດຖາວອນສູງ"
      );
    case "use_installed_app":
      return "ເປີດແອັບຈາກໄອຄອນ PWA ທີ່ຕິດຕັ້ງແລ້ວ ບໍ່ແມ່ນເປີດຜ່ານແທັບເບຣາວເຊີ";
    case "secure_context_required":
      return "ຕ້ອງເຂົ້າແອັບຜ່ານ HTTPS (ຫຼື localhost) — HTTP ບໍ່ໄດ້ຮັບສິດເກັບຖາວອນ";
    case "webview_or_browser_denied":
      return (
        "ເບຣາວເຊີ/WebView ປະຕິເສດ persist() ແມ້ກົດຂໍແລ້ວ — " +
        "ຖ້າເປັນ WebView ໃນແອັບ Android/iOS ຕ້ອງຕັ້ງຄ່າ native ໃຫ້ເກັບ DOM/IndexedDB ຖາວອນ " +
        "ແລະຢ່າລຶບ cache ເມື່ອປິດແອັບ"
      );
    case "unsupported":
      return (
        "ອຸປະກອນນີ້ບໍ່ຮອງຮັບ Persistent Storage API — " +
        "ແນະນຳໃຫ້ໃຊ້ Chrome/Edge ຫຼືຕິດຕັ້ງ PWA"
      );
  }
}

/**
 * Requests durable (persistent) origin storage so the browser/WebView does not
 * evict IndexedDB + localStorage under storage pressure or on device restart.
 *
 * Without this, storage is "best-effort" and a POS device that loses power can
 * come back with an empty IndexedDB — wiping unsynced offline orders.
 *
 * Note: persist() does NOT show a permission dialog. The browser decides silently
 * based on heuristics (PWA install, engagement, secure context). Returning false
 * is normal when opened as a regular browser tab.
 *
 * Safe to call multiple times; it is a no-op when already persisted or when the
 * API is unavailable.
 */
export async function ensurePersistentStorage(): Promise<
  PersistentStorageContext & { requested: boolean }
> {
  if (
    typeof navigator === "undefined" ||
    !navigator.storage ||
    typeof navigator.storage.persist !== "function"
  ) {
    return { ...detectPersistentStorageContext(false), requested: false };
  }

  try {
    const alreadyPersisted =
      typeof navigator.storage.persisted === "function"
        ? await navigator.storage.persisted()
        : false;
    if (alreadyPersisted) {
      return {
        ...detectPersistentStorageContext(true),
        requested: false,
      };
    }

    const persisted = await navigator.storage.persist();
    const context = detectPersistentStorageContext(persisted);
    if (!persisted) {
      console.warn(
        "[persistent-storage] Browser denied persistent storage.",
        {
          guidance: getPersistentStorageGuidance(context.guidanceKey),
          isStandalone: context.isStandalone,
          isSecureContext: context.isSecureContext,
        },
      );
    }
    return { ...context, requested: true };
  } catch (error) {
    console.warn("[persistent-storage] persist() request failed", error);
    return {
      ...detectPersistentStorageContext(false),
      requested: true,
    };
  }
}

export async function getPersistentStorageContext(): Promise<PersistentStorageContext> {
  if (typeof navigator === "undefined" || !navigator.storage) {
    return detectPersistentStorageContext(false);
  }
  const persisted =
    typeof navigator.storage.persisted === "function"
      ? await navigator.storage.persisted()
      : false;
  return detectPersistentStorageContext(persisted);
}

/**
 * Returns the current storage usage estimate, useful for diagnosing eviction.
 */
export async function getStorageEstimate(): Promise<{
  usage?: number;
  quota?: number;
  persisted: boolean;
}> {
  if (typeof navigator === "undefined" || !navigator.storage) {
    return { persisted: false };
  }
  const [estimate, persisted] = await Promise.all([
    typeof navigator.storage.estimate === "function"
      ? navigator.storage.estimate()
      : Promise.resolve({} as StorageEstimate),
    typeof navigator.storage.persisted === "function"
      ? navigator.storage.persisted()
      : Promise.resolve(false),
  ]);
  return {
    usage: estimate.usage,
    quota: estimate.quota,
    persisted,
  };
}
